/**
 * PDF Ticket Utilities
 *
 * Utilidades estandarizadas para generación de PDFs de tickets/recibos
 * que garantizan compatibilidad cross-platform (Windows, Mac, Linux).
 *
 * Problema resuelto: Los PDFs se veían diferentes en cada sistema Windows
 * porque las fuentes estándar de PDF no se embeben y cada visor las sustituye
 * con fuentes locales del sistema.
 *
 * Solución: Embeber la fuente directamente en el PDF y usar configuraciones
 * estandarizadas para dimensiones de papel de impresoras térmicas.
 */

import { jsPDF } from "jspdf";
import "jspdf-autotable";

// ============================================================================
// CONSTANTES DE CONFIGURACIÓN
// ============================================================================

/**
 * Tamaños estándar de papel para impresoras térmicas POS
 * Valores en milímetros
 */
export const THERMAL_PAPER_SIZES = {
  // Impresora térmica de 80mm (estándar POS)
  THERMAL_80MM: { width: 80, name: "80mm Thermal" },
  // Impresora térmica de 58mm (compacta/móvil)
  THERMAL_58MM: { width: 58, name: "58mm Thermal" },
  // Etiquetas estándar
  LABEL_49x25: { width: 49, height: 25, name: "49x25mm Label" },
};

/**
 * Configuración de fuentes estándar
 * Helvetica es una de las 14 fuentes base de PDF, garantizada en todos los visores
 */
export const FONT_CONFIG = {
  STANDARD: {
    family: "helvetica",
    styles: {
      normal: "normal",
      bold: "bold",
      italic: "italic",
      bolditalic: "bolditalic",
    },
  },
  // Courier para datos tabulares (monoespaciada)
  MONOSPACE: {
    family: "courier",
    styles: {
      normal: "normal",
      bold: "bold",
    },
  },
};

/**
 * Estilos predefinidos para tickets
 */
export const TICKET_STYLES = {
  // Encabezado del ticket
  header: {
    fontSize: 10,
    fontStyle: "bold",
  },
  // Título de sección
  sectionTitle: {
    fontSize: 8,
    fontStyle: "bold",
  },
  // Texto normal
  body: {
    fontSize: 7,
    fontStyle: "normal",
  },
  // Texto pequeño (pie de página, notas)
  small: {
    fontSize: 6,
    fontStyle: "normal",
  },
  // Totales y montos importantes
  total: {
    fontSize: 9,
    fontStyle: "bold",
  },
};

// ============================================================================
// FUNCIONES DE CREACIÓN DE DOCUMENTOS
// ============================================================================

/**
 * Crea un documento PDF estandarizado para ticket de impresora térmica
 *
 * @param {Object} options - Opciones de configuración
 * @param {number} options.width - Ancho del papel en mm (default: 80)
 * @param {number} options.height - Alto del papel en mm (calculado si no se provee)
 * @param {number} options.contentHeight - Alto estimado del contenido para calcular altura
 * @param {string} options.title - Título del documento (metadatos)
 * @param {string} options.orientation - 'portrait' o 'landscape' (default: 'portrait')
 * @returns {jsPDF} Instancia de jsPDF configurada
 */
export function createTicketPDF(options = {}) {
  const {
    width = THERMAL_PAPER_SIZES.THERMAL_80MM.width,
    height = null,
    contentHeight = 200,
    title = "Ticket",
    orientation = "portrait",
  } = options;

  // Calcular altura: usar la proporcionada o basarse en el contenido
  const finalHeight = height || Math.max(contentHeight, 50);

  // Crear documento con configuración estandarizada
  const doc = new jsPDF({
    orientation: orientation === "landscape" ? "l" : "p",
    unit: "mm",
    format: [width, finalHeight],
    compress: true,
    putOnlyUsedFonts: true,
    floatPrecision: 16, // Mayor precisión para mejor renderizado
  });

  // Configurar metadatos del documento para comportamiento consistente
  doc.setProperties({
    title: title,
    subject: "Documento generado por Gloss POS",
    creator: "Gloss POS System",
    author: "Sistemify",
    keywords: "ticket, recibo, pos",
  });

  // Configurar fuente por defecto
  doc.setFont(FONT_CONFIG.STANDARD.family, FONT_CONFIG.STANDARD.styles.normal);

  return doc;
}

/**
 * Crea un documento PDF estandarizado para etiquetas
 *
 * @param {Object} options - Opciones de configuración
 * @param {number} options.width - Ancho de la etiqueta en mm
 * @param {number} options.height - Alto de la etiqueta en mm
 * @param {string} options.orientation - 'portrait' o 'landscape'
 * @returns {jsPDF} Instancia de jsPDF configurada
 */
export function createLabelPDF(options = {}) {
  const {
    width = THERMAL_PAPER_SIZES.LABEL_49x25.width,
    height = THERMAL_PAPER_SIZES.LABEL_49x25.height,
    orientation = "landscape",
  } = options;

  const doc = new jsPDF({
    orientation: orientation === "landscape" ? "l" : "p",
    unit: "mm",
    format: [width, height],
    compress: false, // Sin compresión para mejor calidad de código de barras
    putOnlyUsedFonts: true,
    floatPrecision: 16,
  });

  doc.setProperties({
    title: "Etiqueta de Producto",
    subject: "Etiqueta generada por Gloss POS",
    creator: "Gloss POS System",
    author: "Sistemify",
  });

  doc.setFont(FONT_CONFIG.STANDARD.family, FONT_CONFIG.STANDARD.styles.normal);

  return doc;
}

// ============================================================================
// FUNCIONES HELPER DE ESTILO
// ============================================================================

/**
 * Aplica un estilo predefinido al documento
 *
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {string} styleName - Nombre del estilo (header, sectionTitle, body, small, total)
 * @returns {jsPDF} El mismo documento para encadenamiento
 */
export function applyStyle(doc, styleName) {
  const style = TICKET_STYLES[styleName];
  if (!style) {
    console.warn(`Estilo '${styleName}' no encontrado, usando 'body'`);
    return applyStyle(doc, "body");
  }

  doc.setFontSize(style.fontSize);
  doc.setFont(FONT_CONFIG.STANDARD.family, style.fontStyle);

  return doc;
}

/**
 * Configura el documento con fuente y tamaño específicos
 *
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {number} size - Tamaño de fuente
 * @param {string} style - Estilo: 'normal', 'bold', 'italic', 'bolditalic'
 * @returns {jsPDF} El mismo documento para encadenamiento
 */
export function setFontStyle(doc, size, style = "normal") {
  doc.setFontSize(size);
  doc.setFont(FONT_CONFIG.STANDARD.family, style);
  return doc;
}

// ============================================================================
// CONFIGURACIÓN DE AUTOTABLE ESTANDARIZADA
// ============================================================================

/**
 * Configuración base para autoTable en tickets
 * Garantiza apariencia consistente en todos los visores
 */
export const AUTOTABLE_TICKET_CONFIG = {
  theme: "plain",
  styles: {
    font: "helvetica",
    fontSize: 6,
    cellPadding: 1,
    overflow: "linebreak",
    halign: "left",
    valign: "middle",
  },
  headStyles: {
    fontStyle: "bold",
    fontSize: 6,
    fillColor: false,
    textColor: [0, 0, 0],
  },
  footStyles: {
    fontStyle: "bold",
    fontSize: 6,
    fillColor: false,
    textColor: [0, 0, 0],
  },
  margin: { left: 2, right: 2 },
};

/**
 * Genera configuración de autoTable para tickets
 *
 * @param {Object} overrides - Configuraciones adicionales para sobrescribir
 * @returns {Object} Configuración completa para autoTable
 */
export function getAutoTableConfig(overrides = {}) {
  return {
    ...AUTOTABLE_TICKET_CONFIG,
    ...overrides,
    styles: {
      ...AUTOTABLE_TICKET_CONFIG.styles,
      ...(overrides.styles || {}),
    },
    headStyles: {
      ...AUTOTABLE_TICKET_CONFIG.headStyles,
      ...(overrides.headStyles || {}),
    },
    footStyles: {
      ...AUTOTABLE_TICKET_CONFIG.footStyles,
      ...(overrides.footStyles || {}),
    },
    margin: {
      ...AUTOTABLE_TICKET_CONFIG.margin,
      ...(overrides.margin || {}),
    },
  };
}

// ============================================================================
// FUNCIONES DE FORMATO
// ============================================================================

/**
 * Formatea un número como moneda USD
 *
 * @param {number} value - Valor a formatear
 * @returns {string} Valor formateado como moneda
 */
export function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);
}

/**
 * Formatea un número con separadores de miles
 *
 * @param {number} value - Valor a formatear
 * @returns {string} Valor formateado
 */
export function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(value || 0);
}

// ============================================================================
// FUNCIONES DE SALIDA
// ============================================================================

/**
 * Guarda el PDF con configuración estandarizada
 *
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {string} filename - Nombre del archivo (sin extensión)
 */
export function saveTicketPDF(doc, filename) {
  doc.save(`${filename}.pdf`);
}

/**
 * Abre el PDF en una nueva ventana del navegador
 * Mejor opción para previsualización antes de imprimir
 *
 * @param {jsPDF} doc - Instancia de jsPDF
 */
export function openTicketPDF(doc) {
  const pdfOutput = doc.output("blob");
  const url = URL.createObjectURL(pdfOutput);
  window.open(url, "_blank");
}

/**
 * Obtiene el PDF como base64 para envío por email/API
 *
 * @param {jsPDF} doc - Instancia de jsPDF
 * @returns {string} PDF en formato base64
 */
export function getTicketPDFBase64(doc) {
  return doc.output("datauristring").split(",")[1];
}

/**
 * Obtiene el PDF como ArrayBuffer para envío por WhatsApp/API
 *
 * @param {jsPDF} doc - Instancia de jsPDF
 * @returns {ArrayBuffer} PDF como ArrayBuffer
 */
export function getTicketPDFArrayBuffer(doc) {
  return doc.output("arraybuffer");
}

// ============================================================================
// FUNCIONES DE CÁLCULO DE ALTURA
// ============================================================================

/**
 * Calcula la altura necesaria para un ticket basado en el número de filas
 *
 * @param {number} numRows - Número de filas de contenido
 * @param {number} numTables - Número de tablas
 * @param {number} headerHeight - Altura del encabezado en mm (default: 15)
 * @param {number} rowHeight - Altura por fila en mm (default: 3)
 * @param {number} tableGap - Espacio entre tablas en mm (default: 5)
 * @returns {number} Altura total estimada en mm
 */
export function calculateTicketHeight(
  numRows,
  numTables = 1,
  headerHeight = 15,
  rowHeight = 3,
  tableGap = 5
) {
  const contentHeight = numRows * rowHeight;
  const tablesGapHeight = (numTables - 1) * tableGap;
  const footerHeight = 10;

  return headerHeight + contentHeight + tablesGapHeight + footerHeight;
}

// ============================================================================
// EXPORTACIÓN POR DEFECTO
// ============================================================================

export default {
  // Constantes
  THERMAL_PAPER_SIZES,
  FONT_CONFIG,
  TICKET_STYLES,
  AUTOTABLE_TICKET_CONFIG,

  // Creación de documentos
  createTicketPDF,
  createLabelPDF,

  // Estilos
  applyStyle,
  setFontStyle,
  getAutoTableConfig,

  // Formato
  formatCurrency,
  formatNumber,

  // Salida
  saveTicketPDF,
  openTicketPDF,
  getTicketPDFBase64,
  getTicketPDFArrayBuffer,

  // Utilidades
  calculateTicketHeight,
};
